import { makeStyles } from "@material-ui/core/styles";
import React, { useContext, useEffect, useState } from "react";
import TestSpecificationDTO from "../../../api/models/test-request/TestSpecificationDTO";
import TmbTestPlanVersion from "../../../api/models/tmb/TmbTestPlanVersion";
import TesaTmbApi from "../../../api/services/TesaTmbApi";
import CustomProgress from "../../../components/Alert/CustomProgress";
import ConfirmationDialogContext from "../../../components/Dialog/ConfirmationDialog/ConfirmationDialogContext";
import CustomDialog from "../../../components/Dialog/CustomDialog";
import InfoBox from "../../../components/InfoBox";
import ErrorSnackbarContext from "../../../contexts/ErrorSnackbarContext";
import uniqueListBy from "../../../functions/uniqueListBy.function";
import AnalyzeHistoricalForm, { AnalyzeHistoricalFormType } from "./AnalyzeHistoricalForm";
import { AnalyzeHistoricalSpecificationData, MinimalSampleReference, TestSpecification } from "./testTab.models";

const useStyles = makeStyles({
  header: {
    marginBottom: "1rem",
    textTransform: "uppercase",
    "& h2": {
      marginBottom: "0",
    },
  },
  loading: {
    "& .MuiCircularProgress-root": {
      position: "fixed",
      margin: "auto",
      top: 0,
      left: 0,
      bottom: 0,
      right: 0,
    },
  },
});

const TITLE = "Analysis Settings";
const INFO =
  "Please select products, substrates and/or test methods to start a search of historical data. " +
  "At least two of the three properties must be selected to start the analysis. " +
  "You can reload the initial settings by hitting the reset button.";

function prepareSpecificationData(
  specifications: TestSpecificationDTO[],
  api: TesaTmbApi,
): Promise<AnalyzeHistoricalSpecificationData> {
  const promises = specifications.map((s) => api.fetchTestPlanVersion(s.testPlanVersionId, s.testPlanVersionVersion));
  return Promise.all(promises).then((responses) => {
    const deprecatedSpecs: TestSpecification[] = [];
    const current = responses
      .reduce<TmbTestPlanVersion[]>((specs, { data: spec }) => {
        if (spec.lifeCycleState === "DEPRECATED") {
          deprecatedSpecs.push({ id: spec.id, testPlanVersionName: spec.name, testPlanVersionVersion: spec.version });
          return specs;
        }
        specs.push(spec);
        return specs;
      }, [])
      .flatMap(({ expectedResults }) =>
        expectedResults.map(({ testMethodId: id, testMethodName: name, testMethodVersion: version }) => ({
          id,
          name,
          version,
        })),
      );
    return {
      testMethods: uniqueListBy(current, "id"),
      deprecatedSpecs,
    };
  });
}

interface AnalyzeHistoricalDialogProps {
  onConfirm: (values: AnalyzeHistoricalFormType) => void;
  onClose: () => void;
  open: boolean;
  productSamples: MinimalSampleReference[];
  settingsCached: AnalyzeHistoricalFormType | null;
  substrateSamples: MinimalSampleReference[];
  testSpecifications: TestSpecificationDTO[];
}

const AnalyzeHistoricalSettingsDialog: React.FC<AnalyzeHistoricalDialogProps> = ({
  open,
  onConfirm: handleConfirm,
  onClose: handleClose,
  productSamples,
  settingsCached,
  substrateSamples,
  testSpecifications,
}) => {
  const [analyzeHistoricalSpecificationData, setAnalyzeHistoricalSpecificationData] =
    useState<AnalyzeHistoricalSpecificationData | null>(null);
  const classes = useStyles();
  const confirmationDialogContext = useContext(ConfirmationDialogContext);
  const { handleHttpError } = useContext(ErrorSnackbarContext);
  const [loading, setLoading] = useState(false);
  const tmbApi = new TesaTmbApi(confirmationDialogContext);

  useEffect(() => {
    if (open && testSpecifications.length) {
      let doUpdate = true;
      setLoading(true);
      prepareSpecificationData(testSpecifications, tmbApi)
        .then((preparedData) => {
          if (doUpdate) setAnalyzeHistoricalSpecificationData(preparedData);
        })
        .catch(handleHttpError)
        .finally(() => setLoading(false));
      return () => {
        doUpdate = false;
      };
    }
  }, [open, testSpecifications]);

  if (loading) {
    return (
      <div className={classes.loading}>
        <CustomProgress />
      </div>
    );
  }

  return (
    <CustomDialog fullWidth maxWidth="md" onClose={handleClose} open={open}>
      <InfoBox className={classes.header} compress text={INFO} TooltipProps={{ placement: "right" }}>
        <h2>{TITLE}</h2>
      </InfoBox>
      <AnalyzeHistoricalForm
        cachedForm={settingsCached}
        onSubmit={(values) => {
          handleConfirm(values);
          handleClose();
        }}
        productSamples={productSamples}
        specificationData={analyzeHistoricalSpecificationData}
        substrateSamples={substrateSamples}
      />
    </CustomDialog>
  );
};

export default AnalyzeHistoricalSettingsDialog;
